<template>
  <div v-if="!!question && !!options">
    <b-row v-if="!hideLabel">
      <b-col>
        <h6>{{ label }}</h6>
        <div class="mb-2" v-html="question.description"></div>
      </b-col>
      <b-col class="col-auto">
        <b-dropdown
          v-if="showAskOtherFields"
          variant="link"
          size="sm"
          right
          :text="trans('fields')"
        >
          <b-dropdown-item
            v-for="(show, attr) in ask_other_fields"
            :key="'field_'+attr"
            :disabled="!!question.options[attr]"
            @click="onClickAskOtherField(attr)"
          >
            <i :class="`fa fa-fw ${show ? 'fa-check' : ''}`"></i>
            {{ !!options[attr+'_label'] ? options[attr+'_label'] : trans(attr == 'medical' ? 'medical_information' : attr) }}
          </b-dropdown-item>
        </b-dropdown>
      </b-col>
      <b-col class="col-auto text-right">
        <small v-if="isDesign && question.type == 'account_owner' && question.options.show_client_connect && !hideConnect">
          <span class="d-block">{{ trans('already_have_an_account') }}</span>
          <b-link href="#" @click.prevent>{{ trans('connect') }}</b-link>
        </small>
        <small v-if="!isDesign && question.type == 'account_owner' && !!organization && !hideConnect" class="d-block">
          <span v-if="!organization.connected_client" class="d-block">
            {{ trans('already_have_an_account') }}
          </span>
          <b-button
            v-if="!organization.connected_client"
            variant="link"
            size="sm"
            class="p-0"
            @click="show_connect = !show_connect"
          >
            {{ trans(show_connect ? 'hide' : 'connect') }}
          </b-button>
          <span v-if="!!organization.connected_client" class="d-block">
            {{ trans('you_are_connected_as').replace(':as', organization.connected_client.name) }}
          </span>
          <b-button
            v-if="!!organization.connected_client"
            variant="link"
            size="sm"
            class="p-0"
            @click="onClickDisconnect"
          >
            <span v-if="disconnect_busy">
              <i class="fa fa-fw fa-circle-notch fa-spin"></i>
              {{ trans('please_wait') }}
            </span>
            <span v-if="!disconnect_busy">{{ trans('disconnect') }}</span>
          </b-button>
        </small>
        <person-dropdown
          v-if="showChoosePerson"
          :client="organization.connected_client"
          :persons="persons"
          @person="onChoosePerson"
          :admin-item="!!organization && !!organization.auth_user ? trans('search_for_client') : null"
          @click-admin-item="onClickAdminChooseClient"
          :hide-memberships="hideMemberships"
          :disabled="busy || lockNameEmail"
          class="ml-2"
        ></person-dropdown>
        <admin-choose-client-modal
          v-if="!!organization && !!organization.auth_user"
          :id="adminChooseClientModalId"
          :organization="organization"
          :type="!hideMemberships && organization.has_memberships ? 'member' : 'client'"
          :hide-memberships="hideMemberships"
          @chosen="onAdminChooseClient"
        ></admin-choose-client-modal>
      </b-col>
    </b-row>
    <connect
      v-if="show_connect"
      :organization="organization"
      :reload-on-success="false"
      @connected="onConnected"
    ></connect>
    <p v-if="!!disconnect_error" class="text-danger text-right">
      {{ disconnect_error }}
    </p>

    <div v-if="proposeNames && metas && metas.length > 0 && !isDesign" class="mb-2">
        <small class="font-italic">
            {{ trans('same_as') }} <span v-for="(meta,index) in metas">
            <a href="#" @click.prevent="onChoosePerson(meta)">{{ meta.name }}</a><span v-if="index != metas.length -1">, </span>
          </span>
        </small>
    </div>

    <b-row v-if="question.type == 'company'">
      <b-col>
        <b-form-group :label="(options.company_label ? options.company_label : trans('company')) + (required && options.company == 'required' ? '*' : '')">
          <b-form-input
            :size="size"
            :name="`${slug}${terminaison('company')}`"
            v-model="company"
            :state="isCompanyValid"
            :required="required"
            :disabled="disabled"
            @focus="$emit('clear-error', `${slug}.company`)"
            @change="emitIfDifferent"
          />
          <b-form-invalid-feedback :state="!!errors.company ? false : null">
            {{ (errors.company||[]).join(' ') }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row cols="1" cols-md="2" v-if="question.type == 'company' && options.other_phone">
      <b-col>
        <b-form-group :label="(options.other_phone_label || trans('telephone')) + (required && options.other_phone == 'required' ? '*' : '')">
          <b-form-input
            :size="size"
            :name="`${slug}${terminaison('other_phone')}`"
            v-model="other_phone"
            :state="isOtherPhoneValid"
            :formatter="phoneFormatter"
            :required="required && options.other_phone == 'required'"
            :disabled="disabled"
            @focus="$emit('clear-error', `${slug}.other_phone`)"
            @change="emitIfDifferent"
            @blur="emitIfDifferent"
          />
          <b-form-invalid-feedback :state="!!errors.other_phone ? false : null">
            {{ (errors.other_phone||[]).join(' ') }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row v-if="options.relationship">
      <b-col>
        <b-form-group :label="trans('relationship') + (required && options.gender == 'required' ? '*' : '')">
          <b-form-select
            :size="size"
            :name="`${slug}${terminaison('relationship')}`"
            v-model="relationship"
            :state="isRelationshipValid"
            :required="required && options.relationship == 'required'"
            :options="relationshipOptions"
            :disabled="disabled"
            @focus="$emit('clear-error', `${slug}.relationship`)"
            @change="emitIfDifferent"
          />
          <b-form-invalid-feedback :state="!!errors.relationship ? false : null">
            {{ (errors.relationship||[]).join(' ') }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row v-if="options.name">
      <b-col class="pr-0">
        <b-form-group :label="trans('first_name') + (required && options.name == 'required' ? '*' : '')">
          <b-form-input
            :size="size"
            :name="`${slug}${terminaison('first_name')}`"
            v-model="first_name"
            :state="isFirstNameValid"
            :required="required"
            :disabled="disabled"
            :readonly="lockFirstName"
            @focus="$emit('clear-error', `${slug}.first_name`)"
            @change="emitIfDifferent"
          />
          <b-form-invalid-feedback :state="!!errors.first_name ? false : null">
            {{ (errors.first_name||[]).join(' ') }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group :label="trans('last_name') + (required && options.name == 'required' ? '*' : '')">
          <b-form-input
            :size="size"
            :name="`${slug}${terminaison('last_name')}`"
            v-model="last_name"
            :state="isLastNameValid"
            :required="required"
            :disabled="disabled"
            :readonly="lockLastName"
            @focus="$emit('clear-error', `${slug}.last_name`)"
            @change="emitIfDifferent"
          />
          <b-form-invalid-feedback :state="!!errors.last_name ? false : null">
            {{ (errors.last_name||[]).join(' ') }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row v-if="question.type != 'company' && options.pronouns">
      <b-col>
        <b-form-group :label="trans('pronouns') + (required && options.pronouns == 'required' ? '*' : '')">
          <b-form-input
            :size="size"
            :name="`${slug}${terminaison('pronouns')}`"
            v-model="pronouns"
            :state="isPronounsValid"
            :required="required"
            :disabled="disabled"
            @focus="$emit('clear-error', `${slug}.pronouns`)"
            @change="emitIfDifferent"
          />
          <b-form-invalid-feedback :state="!!errors.pronouns ? false : null">
            {{ (errors.pronouns||[]).join(' ') }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
      <b-col>
        &nbsp;
      </b-col>
    </b-row>

    <b-row v-if="question.type != 'company' && options.company">
      <b-col>
        <b-form-group :label="(options.company_label ? options.company_label : trans('company')) + (required && options.company == 'required' ? '*' : '')">
          <b-form-input
            :size="size"
            :name="`${slug}${terminaison('company')}`"
            v-model="company"
            :state="isCompanyValid"
            :required="required"
            :disabled="disabled"
            @focus="$emit('clear-error', `${slug}.company`)"
            @change="emitIfDifferent"
          />
          <b-form-invalid-feedback :state="!!errors.company ? false : null">
            {{ (errors.company||[]).join(' ') }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row v-if="options.email || options.mobile">
      <b-col v-if="options.email" :class="options.mobile ? 'pr-0' : ''">
        <b-form-group :label="trans('email') + (required && options.email == 'required' ? '*' : '')">
          <b-form-input
            :size="size"
            :name="`${slug}${terminaison('email')}`"
            type="email"
            v-model="email"
            :state="isEmailValid"
            :required="required && options.email == 'required'"
            :disabled="disabled"
            :readonly="lockEmail"
            @change="onChangeEmail"
            @focus="$emit('clear-error', `${slug}.email`)"
          />
          <b-form-invalid-feedback :state="!!errors.email ? false : null">
            {{ (errors.email||[]).join(' ') }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
      <b-col v-if="options.mobile">
        <b-form-group :label="trans('mobile') + (required && options.mobile == 'required' ? '*' : '')">
          <b-form-input
            :size="size"
            :name="`${slug}${terminaison('mobile')}`"
            v-model="mobile"
            :state="isMobileValid"
            :formatter="phoneFormatter"
            :required="required && options.mobile == 'required'"
            :disabled="disabled"
            :readonly="lockMobile"
            @focus="$emit('clear-error', `${slug}.mobile`)"
            @change="emitIfDifferent"
            @blur="emitIfDifferent"
          />
          <b-form-invalid-feedback :state="!!errors.mobile ? false : null">
            {{ (errors.mobile||[]).join(' ') }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row v-if="options.gender || options.date_of_birth">
      <b-col :class="options.date_of_birth ? 'pr-0' : ''" v-if="options.gender">
        <b-form-group :label="(options.gender_label ? options.gender_label : trans('gender')) + (required && options.gender == 'required' ? '*' : '')">
          <b-form-select
            :size="size"
            :name="`${slug}${terminaison('gender')}`"
            v-model="gender"
            :state="isGenderValid"
            :required="required && options.gender == 'required'"
            :options="genderOptions"
            :disabled="disabled"
            @focus="$emit('clear-error', `${slug}.gender`)"
            @change="emitIfDifferent"
          />
          <b-form-invalid-feedback :state="!!errors.gender ? false : null">
            {{ (errors.gender||[]).join(' ') }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
      <b-col v-if="options.date_of_birth">
        <b-form-group :label="trans('date_of_birth') + (required && options.date_of_birth == 'required' ? '*' : '')">
          <datetime
            v-if="!isLive"
            v-model="date_of_birth"
            :config="dateOptions"
            :state="isDateOfBirthValid"
            :disabled="disabled"
            :name="`${slug}${terminaison('date_of_birth')}`"
            :class="`form-control form-control-${size} ${isDateOfBirthValid === true ? 'is-valid' : (isDateOfBirthValid === false ? 'is-invalid' : '')}`"
            @dp-show="$emit('clear-error', `${slug}.date_of_birth`)"
            @dp-change="emitIfDifferent"
          />
          <b-row v-else>
            <b-col class="col-md-5">
              <b-form-select
                :placeholder="trans('yyyy')"
                :size="size"
                :name="`${slug}${terminaison('yyyy')}`"
                :options="years"
                v-model="yyyy"
                :state="isDateOfBirthValid"
                :required="required && options.date_of_birth == 'required'"
                :disabled="disabled"
                @change="emitIfDifferent"
                class="mb-2 mb-md-0"
              />
            </b-col>
            <b-col class="col-md-4">
              <b-form-select
                :placeholder="trans('mm')"
                :size="size"
                :name="`${slug}${terminaison('mm')}`"
                :options="months"
                v-model="mm"
                :state="isDateOfBirthValid"
                :required="required && options.date_of_birth == 'required'"
                :disabled="disabled"
                @change="emitIfDifferent"
              />
            </b-col>
            <b-col class="col-md-3">
              <b-form-select
                :placeholder="trans('dd')"
                :size="size"
                :name="`${slug}${terminaison('dd')}`"
                :options="days"
                v-model="dd"
                :state="isDateOfBirthValid"
                :required="required && options.date_of_birth == 'required'"
                :disabled="disabled"
                @change="emitIfDifferent"
              />
            </b-col>
            <input type="hidden" :value="dob" :name="`${slug}${terminaison('date_of_birth')}`" />
          </b-row>
          <b-form-text v-if="options.date_of_birth == 'required' && printAgeConstraint(ageConstraint)">
            <span :class="dateOfBirthRespectsAgeConstraint === false ? 'text-danger' : ''">{{ printAgeConstraint(ageConstraint, organization) }}</span>
          </b-form-text>
          <b-form-invalid-feedback :state="!!errors.date_of_birth ? false : null">
            {{ (errors.date_of_birth||[]).join(' ') }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row v-if="options.grade || options.friend">
      <b-col v-if="options.grade" :class="options.friend ? 'pr-0' : ''">
        <b-form-group :label="trans('grade') + (required && options.grade == 'required' ? '*' : '')">
          <b-form-input
            v-if="!options.grade_choices || options.grade_choices.length == 0"
            :size="size"
            :name="`${slug}${terminaison('grade')}`"
            v-model="grade"
            :state="isGradeValid"
            :required="required && options.grade == 'required'"
            :disabled="disabled"
            @focus="$emit('clear-error', `${slug}.grade`)"
            @change="emitIfDifferent"
          />
          <b-form-select
            v-if="!!options.grade_choices && options.grade_choices.length > 0"
            :size="size"
            :name="`${slug}${terminaison('grade')}`"
            v-model="grade"
            :options="gradeOptions"
            :state="isGradeValid"
            :required="required && options.grade == 'required'"
            :disabled="disabled"
            @focus="$emit('clear-error', `${slug}.grade`)"
            @change="emitIfDifferent"
          />
          <b-form-invalid-feedback :state="!!errors.grade ? false : null">
            {{ (errors.grade||[]).join(' ') }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
      <b-col v-if="options.friend">
        <b-form-group :label="(options.friend_pairing_label || trans('friend_pairing')) + (required && options.friend == 'required' ? '*' : '')">
          <b-form-text v-if="!!options.friend_pairing_description">
            {{ options.friend_pairing_description }}
          </b-form-text>
          <b-form-input
            :size="size"
            :name="`${slug}${terminaison('friend')}`"
            v-model="friend"
            :state="isFriendValid"
            :required="required && options.friend == 'required'"
            :disabled="disabled"
            @focus="$emit('clear-error', `${slug}.friend`)"
            @change="emitIfDifferent"
          />
          <b-form-invalid-feedback :state="!!errors.friend ? false : null">
            {{ (errors.friend||[]).join(' ') }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row v-if="options.pickup_password">
      <b-col>
        <b-form-group :label="trans('pickup_password') + (required && options.pickup_password == 'required' ? '*' : '')">
          <b-form-input
            :size="size"
            :name="`${slug}${terminaison('pickup_password')}`"
            v-model="pickup_password"
            :state="isPickupPasswordValid"
            :required="required && options.pickup_password == 'required'"
            :disabled="disabled"
            @focus="$emit('clear-error', `${slug}.pickup_password`)"
            @change="emitIfDifferent"
          />
          <b-form-invalid-feedback :state="!!errors.pickup_password ? false : null">
            {{ (errors.pickup_password||[]).join(' ') }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row cols="1" cols-md="2" v-if="question.type != 'company' && options.other_phone">
      <b-col>
        <b-form-group :label="(options.other_phone_label || trans('other_phone_home_or_work')) + (required && options.other_phone == 'required' ? '*' : '')">
          <b-form-input
            :size="size"
            :name="`${slug}${terminaison('other_phone')}`"
            v-model="other_phone"
            :state="isOtherPhoneValid"
            :formatter="phoneFormatter"
            :required="required && options.other_phone == 'required'"
            :disabled="disabled"
            @focus="$emit('clear-error', `${slug}.other_phone`)"
            @change="emitIfDifferent"
            @blur="emitIfDifferent"
          />
          <b-form-invalid-feedback :state="!!errors.other_phone ? false : null">
            {{ (errors.other_phone||[]).join(' ') }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row cols="1" cols-lg="2" v-if="options.address">
      <b-col class="pr-0">
        <b-form-group :label="trans('address') + (required && options.address == 'required' ? '*' : '')">
          <b-form-input
            :size="size"
            :name="`${slug}${terminaison('address1')}`"
            v-model="address1"
            :state="isAddress1Valid"
            :required="required && options.address == 'required'"
            :disabled="disabled"
            @focus="$emit('clear-error', `${slug}.address1`)"
            @change="emitIfDifferent"
          />
          <b-form-invalid-feedback :state="!!errors.address1 ? false : null">
            {{ (errors.address1||[]).join(' ') }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group :label="trans('city') + (required && options.address == 'required' ? '*' : '')">
          <b-form-input
            :size="size"
            :name="`${slug}${terminaison('city')}`"
            v-model="city"
            :state="isCityValid"
            :required="required && options.address == 'required'"
            :disabled="disabled"
            @focus="$emit('clear-error', `${slug}.city`)"
            @change="emitIfDifferent"
          />
          <b-form-invalid-feedback :state="!!errors.city ? false : null">
            {{ (errors.city||[]).join(' ') }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
      <b-col class="pr-0">
        <b-form-group :label="trans('state_province') + (required && options.address == 'required' ? '*' : '')">
          <b-form-select
            :size="size"
            :name="`${slug}${terminaison('state_province')}`"
            v-model="state_province"
            :options="stateProvinceOptions"
            :state="isStateProvinceValid"
            :required="required && options.address == 'required'"
            :disabled="disabled"
            @focus="$emit('clear-error', `${slug}.state_province`)"
            @change="emitIfDifferent"
          />
          <b-form-invalid-feedback :state="!!errors.state_province ? false : null">
            {{ (errors.state_province||[]).join(' ') }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group :label="trans('zip_postal_code') + (required && options.address == 'required' ? '*' : '')">
          <b-form-input
            :size="size"
            :name="`${slug}${terminaison('zip_postal_code')}`"
            v-model="zip_postal_code"
            :state="isZipPostalCodeValid"
            :required="required && options.address == 'required'"
            :disabled="disabled"
            @focus="$emit('clear-error', `${slug}.zip_postal_code`)"
            @change="emitIfDifferent"
          />
          <b-form-invalid-feedback :state="!!errors.zip_postal_code ? false : null">
            {{ (errors.zip_postal_code||[]).join(' ') }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row cols="1" cols-lg="2" v-if="!options.address && options.zip_postal_code">
      <b-col>
        <b-form-group :label="trans('zip_postal_code') + (required && options.zip_postal_code == 'required' ? '*' : '')">
          <b-form-input
            :size="size"
            :name="`${slug}${terminaison('zip_postal_code')}`"
            v-model="zip_postal_code"
            :state="isZipPostalCodeOnlyValid"
            :required="required && options.zip_postal_code == 'required'"
            :disabled="disabled"
            @focus="$emit('clear-error', `${slug}.zip_postal_code`)"
            @change="emitIfDifferent"
          />
          <b-form-invalid-feedback :state="!!errors.zip_postal_code ? false : null">
            {{ (errors.zip_postal_code||[]).join(' ') }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row v-if="options.medical">
      <b-col>
        <b-form-group :label="(options.medical_label || trans('important_medical_information')) + (required && options.medical == 'required' ? '*' : '')">
          <b-form-text v-if="!!options.medical_description">
            {{ options.medical_description }}
          </b-form-text>
          <b-form-input
            :size="size"
            :name="`${slug}${terminaison('medical')}`"
            v-model="medical"
            :state="isMedicalValid"
            :required="required && options.medical == 'required'"
            :disabled="disabled"
            @focus="$emit('clear-error', `${slug}.medical`)"
            @change="emitIfDifferent"
          />
          <b-form-invalid-feedback :state="!!errors.medical ? false : null">
            {{ (errors.medical||[]).join(' ') }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row v-if="options.medical_conditions">
      <b-col>
        <b-form-group :label="trans('medical_conditions') + (required && options.medical_conditions == 'required' ? '*' : '')">
          <b-form-checkbox-group
            :name="`${slug}${terminaison(`[medical_conditions][]`, true)}`"
            v-model="medical_conditions"
            :required="required && options.medical_conditions == 'required'"
            :options="medicalConditionsOptions"
            :disabled="disabled"
            @focus="$emit('clear-error', `${slug}.medical_conditions`)"
            @change="emitIfDifferent"
          />

          <b-form-invalid-feedback :state="!!errors.medical_conditions ? false : null">
            {{ (errors.medical_conditions||[]).join(' ') }}
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group :label="trans('other')">
          <b-form-input
            :size="size"
            :name="`${slug}${terminaison(`[medical_conditions][]`, true)}`"
            v-model="other_medical_conditions"
            :disabled="disabled"
            @focus="$emit('clear-error', `${slug}.other_medical_conditions`)"
            @change="emitIfDifferent"
          />
        </b-form-group>
      </b-col>
    </b-row>  

    <b-row v-if="options.allergies">
      <b-col>
        <b-form-group :label="trans('allergies') + (required && options.allergies == 'required' ? '*' : '')">
          <b-form-checkbox-group
            :name="`${slug}${terminaison(`[allergies][]`, true)}`"
            v-model="allergies"
            :required="required && options.allergies == 'required'"
            :options="allergiesOptions"
            :disabled="disabled"
            @focus="$emit('clear-error', `${slug}.allergies`)"
            @change="emitIfDifferent"
          />

          <b-form-invalid-feedback :state="!!errors.allergies ? false : null">
            {{ (errors.allergies||[]).join(' ') }}
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group :label="trans('other')">
          <b-form-input
            :size="size"
            :name="`${slug}${terminaison(`[allergies][]`, true)}`"
            v-model="other_allergies"
            :disabled="disabled"
            @focus="$emit('clear-error', `${slug}.other_allergies`)"
            @change="emitIfDifferent"
          />
        </b-form-group>
      </b-col>
    </b-row>

    <b-row v-if="options.can_pickup">
      <b-col>
        <b-form-group>
          <b-form-checkbox
            :size="size"
            :name="`${slug}${terminaison('can_pickup')}`"
            v-model="can_pickup"
            :disabled="disabled"
            @focus="$emit('clear-error', `${slug}.can_pickup`)"
            @change="emitIfDifferent"
          >
            {{ trans('can_pickup') }}
          </b-form-checkbox>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row v-if="options.cc_email">
      <b-col>
        <b-form-group :label="(options.cc_email_label || trans('cc_email_about')) + (required && options.cc_email == 'required' ? '*' : '')">
          <b-form-input
            :size="size"
            :name="`${slug}${terminaison('cc_email')}`"
            type="email"
            v-model="cc_email"
            :state="isCcEmailValid"
            :required="required && options.cc_email == 'required'"
            :disabled="disabled"
            @change="onChangeEmail"
            @focus="$emit('clear-error', `${slug}.cc_email`)"
          />
          <b-form-invalid-feedback :state="!!errors.cc_email ? false : null">
            {{ (errors.cc_email||[]).join(' ') }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row v-if="options.custom_fields">
      <b-col v-for="(custom_field, cindex) in options.custom_fields" :key="cindex" v-if="!!custom_field.required" class="col-12">
        <b-form-group :label="(custom_field.label || trans('custom_field')) + (required && custom_field.required == 'required' ? '*' : '')">
          <b-form-input
            v-if="!custom_field.choices || custom_field.choices.length == 0"
            :size="size"
            :name="`${slug}${terminaison(`[custom_fields][${cindex}]`, true)}`"
            v-model="(custom_fields[cindex] || {}).value"
            :required="required && custom_field.required == 'required'"
            :disabled="disabled"
            @focus="$emit('clear-error', `${slug}.custom_fields[${cindex}]`)"
            @change="emitIfDifferent"
          />
          <b-form-select
            v-if="!!custom_field.choices && custom_field.choices.length > 0"
            :size="size"
            :name="`${slug}${terminaison(`[custom_fields][${cindex}]`, true)}`"
            v-model="(custom_fields[cindex] || {}).value"
            :options="customFieldOptions(cindex)"
            :required="required && custom_field.required == 'required'"
            :disabled="disabled"
            @focus="$emit('clear-error', `${slug}.custom_fields[${cindex}]`)"
            @change="emitIfDifferent"
          />
          <b-form-invalid-feedback :state="!!errors.custom_fields && errors.custom_fields[cindex] ? false : null">
            {{ errors.custom_fields && errors.custom_fields[cindex] ? (errors.custom_fields[cindex] || []).join(' ') : '' }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row v-if="allowPhoto && options.photo">
      <b-col>
        <b-form-group :label="options.photo_label || trans('photo') + (required && options.photo == 'required' ? '*' : '')">
          <file-pond
            :name="`${slug}${terminaison('upload_photo')}`"
            ref="pond"
            :server="photoServerConfig"
            :files="pond_files"
            :label-idle="trans('pond_idle')"
            accepted-file-types="image/jpeg, image/png, image/gif, image/webp, application/pdf"
            :label-file-processing="trans('pond_file_processing')"
            :label-file-type-not-allowed="trans('pond_file_type_not_allowed')"
            :label-file-processing-error="trans('pond_file_processing_error')"
            :label-tap-to-cancel="trans('pond_label_tap_to_cancel')"
            :label-tap-to-retry="trans('pond_label_tap_to_retry')"
            :label-tap-to-undo="trans('pond_label_tap_to_undo')"
            :label-file-processing-complete="trans('pond_label_file_processing_complete')"
            :label-file-processing-aborted="trans('pond_label_file_processing_aborted')"
            :disabled="!!buttonPopoverHtml"
            @init="oninitphoto"
            @processfilestart="onprocessphotofilestart"
            @processfileabort="onprocessphotofileabort"
            @processfile="onprocessphotofile"
            @removefile="onremovephotofile"
          />
          <b-form-invalid-feedback :state="!!errors.photo_umid ? false : null">
            {{ (errors.photo_umid||[]).join(' ') }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row v-if="addons && addons.length > 0">
      <b-col>
        <label class="d-block">
          <strong>{{ options.addons_label || trans('addons') }} {{ options.addons_required ? '*' : '' }}</strong>
        </label>
        <b-row v-for="(addon, index) in addons" :key="'addon-'+index">
          <b-col>
            <b-form-group class="mb-0">
              <b-form-checkbox v-model="addon.quantity" :value="1" :unchecked-value="0" @change="addOnChange(addon, index)" :name="`${slug}${terminaison(`[addons][${index}][quantity]`, true)}`">
                {{ addon.name }}
                <strong v-if="addon.price > 0" class="ml-2"><dollar :value="addon.percentage ? addon.price * price / 100 : addon.price"></dollar></strong>
              </b-form-checkbox>
              <input type="hidden" :name="`${slug}${terminaison(`[addons][${index}][name]`, true)}`" :value="addon.name" />
              <input type="hidden" :name="`${slug}${terminaison(`[addons][${index}][price]`, true)}`" :value="addon.price" />
            </b-form-group>
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <b-row v-if="isDesign && question.type == 'relay'">
      <b-col>
        <b-button variant="branded">
          {{ question.options.relay.button_text || trans('relay_button') }}
        </b-button>
      </b-col>
    </b-row>

    <p v-if="mustBeUnique && !!sameAsPerson" class="text-danger">
      <i class="fa fa-fw fa-exclamation-triangle"></i>
      {{ trans('person_already_used') }}
    </p>

    <membership-text
      v-if="membership !== undefined"
      :membership="membership"
      size="sm"
    ></membership-text>
    <membership-text
      v-if="otherMemberships.length > 0"
      v-for="m in otherMemberships" :key="'other-membership'+m.id"
      :membership="m"
      size="sm"
    ></membership-text>
    <p v-if="!!errors && !!errors['membership']" class="text-danger">
      {{ errors['membership'].join(' ') }}
    </p>
    <br/><br/>

    <p v-if="busy">
      <i class="fa fa-fw fa-circle-notch fa-spin"></i>
      {{ trans('please_wait') }}
    </p>
    <p v-if="!!error" class="text-danger">
      {{ !!error }}
    </p>
    
    <b-alert v-if="showWarnings" :show="question.is_hidden" variant="danger">
      {{ trans('form_hidden_question_warning') }}
    </b-alert>
    <b-alert v-if="showWarnings" :show="question.type == 'account_owner' && question.options.email != 'required'" variant="danger">
      {{ trans('form_anonymous_warning') }}
    </b-alert>
  </div>
</template>

<script>
  import Dollar from './../billing/Dollar'
  import Datetime from 'vue-bootstrap-datetimepicker'
  import MembershipText from './../membership/MembershipText'
  import PersonDropdown from './../client/ClientAccountPersonDropdown'
  import Connect from './../client/Connect'
  import AdminChooseClientModal from './../client/AdminChooseClientModal'

  function buildBlankCustomField() {
    return { label: null, choices: [], required: null }
  }

  function isEmpty(value) {
    return _.isEmpty(value) || ((value||'')+'').trim().length == 0
  }

  const otherFields = [
    'name',
    'email',
    'mobile',
    'gender',
    'date_of_birth',
    'grade',
    'relationship',
    'company',
    'other_phone',
    'address',
    'zip_postal_code',
    'pickup_password',
    'friend',
    'medical',
    'medical_conditions',
    'allergies',
  ]
  function buildAskOtherFieldOptions(options) {
    return _.reduce(otherFields, (result, name) => {
      result[name] = options[name]
      return result
    }, {})
  }

  export default {
    props: {
      organization: Object,
      isDesign: {
        type: Boolean,
        default: false,
      },
      buttonPopoverHtml: String,
      value: Object, // answer
      question: Object,
      hideLabel: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      hideConnect: {
        type: Boolean,
        default: false,
      },
      size: {
        type: String,
        default: 'md',
      },
      errors: {
        type: Object,
        default() {return {}},
      },
      initialValue: Object,
      showWarnings: {
        type: Boolean,
        default: false,
      },
      lockNameEmail: {
        type: Boolean,
        default: false,
      },
      proposeNames: {
        type: Boolean,
        default: false,
      },
      personAnswers: {
        type: Array,
        default() { return []},
      },
      possibleMemberships: Array,
      hideMemberships: {
        type: Boolean,
        default: false,
      },
      contextPackage: Object,
      allowPhoto: {
        type: Boolean,
        default: false,
      },
      showAskOtherFields: {
        type: Boolean,
        default: false,
      },
      isLive: {
        type: Boolean,
        default: false,
      },
    },
    components: {
      Datetime,
      MembershipText,
      PersonDropdown,
      Connect,
      AdminChooseClientModal,
      Dollar,
    },
    data() {
      return {
        trans: trans,
        busy: false,
        error: null,
        moment: moment,
        metas: [],
        printAgeConstraint: _.printAgeConstraint,
        disconnect_busy: false,
        disconnect_error: null,
        first_name: null,
        last_name: null,
        name: null,
        email: null,
        mobile: null,
        other_phone: null,
        address1: null,
        city: null,
        state_province: this.organization ? this.organization.taxable_province : null,
        zip_postal_code: null,
        cc_email: null,
        gender: null,
        date_of_birth: null,
        dateOptions: {
          locale: window.lang,
          format: 'LL',
          viewMode: 'years',
          showClear: true,
        },
        grade: null,
        allergies: [],
        other_allergies: null,
        medical_conditions: [],
        other_medical_conditions: null,
        pickup_password: null,
        friend: null,
        medical: null,
        countryOptions: [
          {value: 'CA', text: trans('canada')},
          {value: 'US', text: trans('usa')},
        ],
        provinces: _.provinces,
        stateProvinceOptions: _.stateProvinceOptions,
        lockFirstName: false,
        lockLastName: false,
        lockEmail: false,
        lockMobile: false,
        relationship: null,
        pronouns: null,
        company: null,
        can_pickup: false,
        show_connect: false,
        chosen_person: null,
        fetched_memberships: {}, // map email to array of memberships
        photo_umid: undefined,
        photo_file: undefined,
        pond_files: [],
        ask_other_fields: buildAskOtherFieldOptions({}),
        custom_fields: [buildBlankCustomField()],
        addons: [],
        yyyy: null,
        mm: null,
        dd: null,
        dob: null,
      }
    },
    computed: {
      years() {
        const years = [{value: null, text: this.trans('yyyy')}]
        for (let i = 2025; i >= 1900; i--) {
          years.push({value: i, text: i})
        }
        return years
      },
      months() {
        const months = [{value: null, text: this.trans('mm')}]
        for (let i = 1; i <= 12; i++) {
          months.push({value: i < 10 ? '0' + i : i, text: i})
        }
        return months
      },
      days() {
        const days = [{value: null, text: this.trans('dd')}]
        for (let i = 1; i <= 31; i++) {
          days.push({value: i < 10 ? '0' + i : i, text: i})
        }
        return days
      },
      photoServerConfig() {
        return {
          process: (this.url || window.location.href).split('?')[0] + `/upload_image`,
          revert: (this.url || window.location.href).split('?')[0] + '/delete_uploaded_media',
          fetch: null,
          headers: {
              'X-CSRF-TOKEN': _.csrf,
          },
        }
      },
      defaultStateProvince() {
        return this.organization ? this.organization.taxable_province : null
      },
      allergiesOptions() {
        let choices = _.clone(this.question.options.allergie_choices || _.allAllergyChoices)
        if (!choices.includes('none')) {
          choices = ['none', ...choices]
        }
        return _.map(choices, allergy => {
          return {
            value: allergy,
            text: allergy ? trans('allergies_' + allergy) : ''
          }
        })
      },
      medicalConditionsOptions() {
        let choices = _.clone(this.question.options.medical_condition_choices || _.allMedicalConditionChoices)
        if (!choices.includes('none')) {
          choices = ['none', ...choices]
        }
        return _.map(choices, medical => {
          return {
            value: medical,
            text: medical ? trans('medical_' + medical) : ''
          }
        })
      },
      genderOptions() {
        let choices = _.clone(this.question.options.gender_choices || _.allGenderChoices)
        if (this.question.options.gender != 'required') choices.unshift(null)
        return _.map(choices, gender => {
          return {
            value: gender,
            text: gender ? trans('gender_' + gender) : ''
          }
        })
      },
      relationshipOptions() {
        let choices = _.clone(this.question.options.relationship_choices || _.allRelationshipChoices)
        if (this.question.options.relationship != 'required') choices.unshift(null)
        return _.map(choices, relationship => {
          return {
            value: relationship,
            text: relationship ? trans('contact_type_' + relationship) : ''
          }
        })
      },
      gradeOptions() {
        let choices = !!this.question.options.grade_choices ?
          _.compact(this.question.options.grade_choices) :
          []
        if (this.question.options.grade != 'required') choices.unshift(null)
        return _.map(choices, grade => {
          return {
            value: grade,
            text: grade || '',
          }
        })
      },
      persons() {
        if (!this.organization || !this.organization.connected_client)
          return []
        if (this.question.type == 'account_owner')
          return []
        if (['other_parent'].indexOf(this.question.type) >= 0)
          return this.organization.connected_client.other_parents || []
        if (['contacts'].indexOf(this.question.type) >= 0)
          return this.organization.connected_client.contacts || []
        if (['company'].indexOf(this.question.type) >= 0)
          return this.organization.connected_client.companies || []
        return this.organization.connected_client.account_members
      },
      showChoosePerson() {
        return !!this.organization && !this.isDesign && (!!this.persons && this.persons.length > 0 || !!this.organization.auth_user)
      },
      memberships() {
        if (this.hideMemberships) return []
        let best_ids = !!this.contextPackage && !!this.contextPackage.memberships ?
          _.map(this.contextPackage.memberships, 'id') :
          []
        best_ids = !!this.contextPackage ? _.reduce(this.contextPackage.addons || [], (best_ids, a) => {
          return _.reduce(a.membership_ids || [], (best_ids, id) => {
            if (best_ids.indexOf(id) === -1) best_ids.push(id)
            return best_ids
          }, best_ids)
        }, best_ids) : []
        if (best_ids.length == 0) best_ids = null
        let memberships = _.reduce(this.persons, (memberships, contact) => {
          if (!!contact.membership &&
            (!best_ids || best_ids.indexOf(contact.membership.id) >= 0)) {
            memberships.push(_.assign({
              recipient_name: contact.name,
              recipient_first_name: contact.first_name,
              recipient_last_name: contact.last_name,
              recipient_email: contact.email
            }, contact.membership))
          }
          return _.reduce(contact.memberships || [], (memberships, m) => {
            if (!!best_ids && best_ids.indexOf(membership.id) === -1) return memberships
            memberships.push(_.assign({
              recipient_name: contact.name,
              recipient_first_name: contact.first_name,
              recipient_last_name: contact.last_name,
              recipient_email: contact.email
            }, m))
            return memberships
          }, memberships)
        }, [])
        return _.reduce(_.values(this.fetched_memberships), (memberships, many) => {
          return _.reduce(many, (memberships, m) => {
            if (!!best_ids && best_ids.indexOf(m.id) === -1) return memberships
            memberships.push(m)
            return memberships
          }, memberships)
        }, memberships)
      },
      membership() {
        if (this.hideMemberships) return undefined
        if (this.chosen_person) {
          let membership = null
          if (this.chosen_person.membership)
            membership = _.clone(this.chosen_person.membership)
          if (this.chosen_person.memberships &&
            this.chosen_person.memberships.length > 0)
            membership = _.clone(this.chosen_person.memberships[0])
          if (!!membership) {
            if (!!membership.recipient_id) {
              return _.find(this.memberships, {recipient_id: membership.recipient_id}) || membership
            }
            return membership
          }
        }
        if (!this.email) return undefined
        return _.reduce(this.memberships, (found, m) => {
          let email = m.email || m.recipient_email
          if (!email ||
            !this.email ||
            email.toLowerCase().trim() != this.email.toLowerCase().trim())
            return found
          if (!found) {
            found = m
          } else {
            if (!!found.expired_at &&
              !!m.expired_at &&
              moment(m.expired_at).isAfter(found.expired_at))
              found = m
          }
          return found
        }, undefined)
      },
      otherMemberships() {
        if (this.hideMemberships) return []
        let used_membership_ids = []
        return _.reduce(this.possibleMemberships || [], (others, m) => {
          if (!!this.membership && m.id == this.membership.id || m.is_expired) return others
          if (used_membership_ids.indexOf(m.id) === -1 &&
            _.camelCase(m.recipient_email).replace('-', '') == _.camelCase(this.email).replace('-', '') &&
            _.camelCase(m.recipient_first_name).replace('-', '') == _.camelCase(this.first_name).replace('-', '') &&
            _.camelCase(m.recipient_last_name).replace('-', '') == _.camelCase(this.last_name).replace('-', '')) {
            others.push(m)
            used_membership_ids.push(m.id)
          }
          return others
        }, [])
      },
      adminChooseClientModalId() {
        return `admin-choose-client-modal_${this.question.id}`
      },
      options() {
        if (!this.showAskOtherFields) return this.question.options
        let options = _.clone(this.question.options)
        _.each(this.ask_other_fields, (show, attr) => {
          if (show && !options[attr]) options[attr] = 'optional'
        })
        return options
      },
      label() {
        return this.question.label
      },
      slug() {
        return this.question.slug
      },
      required() {
        return this.question.required ||
          !this.question.required && (!!this.first_name || !!this.last_name || !!this.email)
      },
      description() {
        return this.question.description
      },
      mustBeUnique() {
        return this.question.options.must_be_unique || false
      },
      sameAsPerson() {
        return _.reduce(this.personAnswers || [], (found, a) => {
          if (!!found || a.question_id == this.question.id) return found
          return _.isSamePerson(this, a) ? a : null
        }, null)
      },
      isFirstNameValid() {
        if (this.errors.first_name) return false
        if (this.first_name) return true
        return null
      },
      isLastNameValid() {
        if (this.errors.last_name) return false
        if (this.last_name) return true
        return null
      },
      isEmailValid() {
        if (this.errors.email) return false
        if (isEmpty(this.email)) return null
        return _.validateEmail(this.email)
      },
      isCcEmailValid() {
        if (this.errors.cc_email) return false
        if (isEmpty(this.cc_email)) return null
        return _.validateEmail(this.cc_email)
      },
      isMobileValid() {
        if (this.errors.mobile) return false
        if (!this.options.mobile || this.isNoClient) return true
        if (isEmpty(this.mobile)) return null
        return _.validatePhone(this.mobile)
      },
      isOtherPhoneValid() {
        if (this.errors.other_phone) return false
        if (!this.options.other_phone || this.isNoClient) return true
        if (isEmpty(this.other_phone)) return null
        return _.validatePhone(this.other_phone)
      },
      isAddress1Valid() {
        if (this.errors.address1) return false
        if (this.address1) return true
        return null
      },
      isCityValid() {
        if (this.errors.city) return false
        if (this.city) return true
        return null
      },
      isStateProvinceValid() {
        if (this.errors.state_province) return false
        if (this.state_province) return true
        return null
      },
      isZipPostalCodeValid() {
        if (this.errors.zip_postal_code) return false
        if (this.zip_postal_code) return true
        return null
      },
      isGenderValid() {
        if (this.errors.gender) return false
        if (this.gender) return true
        return null
      },
      isRelationshipValid() {
        if (this.errors.relationship) return false
        if (this.relationship) return true
        return null
      },
      isPronounsValid() {
        if (this.errors.pronouns) return false
        if (this.pronouns) return true
        return null
      },
      isCompanyValid() {
        if (this.errors.company) return false
        if (this.company) return true
        return null
      },
      ageConstraint() {
        return this.question && this.question.options && this.question.options.age_constraint ?
          this.question.options.age_constraint :
          null
      },
      dateOfBirthRespectsAgeConstraint() {
        return _.validateAgeConstraint(moment(this.date_of_birth, 'LL').toISOString(), this.ageConstraint)
      },
      isDateOfBirthValid() {
        if (this.errors.date_of_birth) return false
        if (isEmpty(this.date_of_birth)) return null
        if (this.dateOfBirthRespectsAgeConstraint === false) return false
        return !isEmpty(this.date_of_birth) && moment(this.date_of_birth, 'LL').diff(moment(), 'days') < 0
      },
      isGradeValid() {
        if (this.errors.grade) return false
        if (this.grade) return true
        return null
      },
      isPickupPasswordValid() {
        if (this.errors.pickup_password) return false
        if (this.pickup_password) return true
        return null
      },
      isFriendValid() {
        if (this.errors.friend) return false
        if (this.friend) return true
        return null
      },
      isMedicalValid() {
        if (this.errors.medical) return false
        if (this.medical) return true
        return null
      },
      isSpecialInstructions() {
        if (this.errors.special_instructions) return false
        if (!this.options.special_instructions || this.isNoClient) return true
        return null
      },
      isAddressValid() {
        if (this.isAddress1Valid === true && this.isCityValid === true && this.isStateProvinceValid === true && this.isZipPostalCodeValid === true) return true
        if (this.isAddress1Valid === null && this.isCityValid === null && this.isStateProvinceValid === null && this.isZipPostalCodeValid === null) return null
        return false
      },
      isZipPostalCodeOnlyValid() {
        if (this.errors.zip_postal_code) return false
        if (this.zip_postal_code) return true
        return null
      },
      address() {
        let country = null
        if (this.provinces.indexOf(this.state_province) >= 0) country = 'CA'
        else if (this.state_province) country = 'US'
        return _.compact([this.address1, this.city, this.state_province, this.zip_postal_code, country]).join(', ')
      },
      isValid() {
        return this.isFirstNameValid !== false && this.isLastNameValid !== false && this.isMobileValid !== false && this.isEmailValid !== false && this.isOtherPhoneValid !== false &&
          this.isAddress1Valid !== false && this.isCityValid !== false && this.isStateProvinceValid !== false && this.isZipPostalCodeValid !== false &&
          this.isAddressValid !== false && this.isSpecialInstructions !== false &&
          this.isZipPostalCodeOnlyValid !== false
      },
      recipient_id() {
        return this.chosen_person ? this.chosen_person.id : undefined
      },
      client_id() {
        return this.chosen_person ? this.chosen_person.client_id : undefined
      },
    },
    watch: {
      yyyy() {
        this.dob = `${this.yyyy}-${this.mm}-${this.dd}`
        if (this.yyyy && this.mm && this.dd && this.isLive) this.date_of_birth = this.dob
      },
      mm() {
        this.dob = `${this.yyyy}-${this.mm}-${this.dd}`
        if (this.yyyy && this.mm && this.dd && this.isLive) this.date_of_birth = this.dob
      },
      dd() {
        this.dob = `${this.yyyy}-${this.mm}-${this.dd}`
        if (this.yyyy && this.mm && this.dd && this.isLive) this.date_of_birth = this.dob
      },
      membership() {
        this.$emit('clear-error', `${this.slug}.membership`)
        this.maybeUpdateNameFromMembership()
        this.emitIfDifferent()
      },
      value() {
        if (!_.isEqual(this.value, this.serialize)) this.hydrate()
      },
      question() {
        this.ask_other_fields = buildAskOtherFieldOptions(!!this.question ? this.question.options || {} : {})
      },
      addons: {
        handler() {
          this.emitIfDifferent()
        },
        deep: true
      },
    },
    methods: {
      addOnChange(addon, index) {
        Vue.set(this.addons, index, addon) // this.addons[index] not working!!!
      },
      onChangeAddon(addon, index) {
        _.each(addon.addons, addon => {
          if (addon.quantity == 0) addon.quantity = 0
          addon.disabled = addon.quantity == 0
        })
      },
      terminaison(field, force) {
        if (force) return field
        return this.isLive ? `[${field}]` : `.${field}`
      },
      onClickAskOtherField(attr) {
        this.ask_other_fields[attr] = !this.ask_other_fields[attr]
      },
      oninitphoto() {
        // Nothing to do here - already done in hydrate
      },
      onprocessphotofilestart() {
        this.$emit('clear-error', this.question.slug)
        this.$emit('busy', true)
      },
      onprocessphotofileabort() {
        this.$emit('busy', false)
      },
      onprocessphotofile(error, file) {
        this.$emit('busy', false)
        //this.answer = this.serializePhotoFiles()
      },
      onremovephotofile(error, file) {
        this.$emit('busy', false)
        //this.answer = this.serializePhotoFiles()
      },
      serializePhotoFile() {
        if (!this.$refs.pond) return this.photo_file
        return this.$refs.pond.getFiles().map(file => {
          return {
            umid: file.serverId,
            name: file.filename,
            size: file.fileSize,
            //type: ?? // mime type is not available here - will be set by server
          }
        })[0] || null
      },
      emitIfDifferent() {
        let answer = this.serialize()
        if (!_.isEqual(this.value, answer)) {
          this.$emit('input', answer)
        }
      },
      onChangePhoto() {

      },
      onChangeEmail() {
        if (this.chosen_person && !_.isSamePerson(this, this.chosen_person)) this.chosen_person = null
        this.$nextTick(() => {
          this.fetchValidMembershipForPerson()
          this.emitIfDifferent()
        })
      },
      onClickDisconnect() {
        this.disconnect_busy = true
        this.disconnect_error = null
        let url = `/org/${this.organization.id}/client/disconnect`
        axios.get(url)
        .then(response => {
          this.disconnect_busy = false
          this.$emit('client', null)
        })
        .catch(error => {
          this.disconnect_busy = false
          this.disconnect_error = _.errorFromAjax(error)
        })
      },
      onConnected(response) {
        this.$emit('client', response.client)
        this.show_connect = false
        this.$nextTick(this.hydrate)
      },
      onClickAdminChooseClient() {
        this.$bvModal.show(this.adminChooseClientModalId)
      },
      onAdminChooseClient(client) {
        this.onChoosePerson(!!client.recipient ? client.recipient : client)
        this.$nextTick(() => {
          this.fetchValidMembershipForPerson()
          this.emitIfDifferent()
        })
      },
      deserializeAllergies(value) {
        let allergies = []
        let other_allergies = []
        _.each(value || [], v => {
          if (_.allAllergyChoices.indexOf(v) >= 0) {
            allergies.push(v)
          } else {
            other_allergies.push(v)
          }
        })
        this.allergies = allergies
        this.other_allergies = other_allergies.join(', ')
      },
      deserializeMedicalConditions(value) {
        let medical_conditions = []
        let other_medical_conditions = []
        _.each(value || [], v => {
          if (_.allMedicalConditionChoices.indexOf(v) >= 0) {
            medical_conditions.push(v)
          } else {
            other_medical_conditions.push(v)
          }
        })
        this.medical_conditions = medical_conditions
        this.other_medical_conditions = other_medical_conditions.join(', ')
      },
      onChoosePerson(recipient) {
        this.chosen_person = _.cloneDeep(recipient)
        let [full_name, first_name, last_name] = _.splitFullName(recipient.name)
        this.first_name = recipient.first_name || first_name
        this.last_name = recipient.last_name || last_name
        _.each([
            'email',
            'mobile',
            'relationship',
            'pronouns',
            'company',
            'gender',
            'date_of_birth',
            'grade',
            'pickup_password',
            'friend',
            'medical',
            'other_phone',
            'address1',
            'city',
            'state_province',
            'zip_postal_code',
          ], option => {
          if (this.options[option]) this[option] = recipient[option] || null
        })
        this.deserializeMedicalConditions(recipient['medical_conditions'] || [])
        this.deserializeAllergies(recipient['allergies'] || [])
        this.$nextTick(() => {
          this.emitIfDifferent()          
        })
      },
      phoneFormatter(phone) {
        return _.formatPhone(phone)
      },
      customFieldOptions(index) {
        let choices = !!this.question.options.custom_fields[index].choices ?
          _.compact(this.question.options.custom_fields[index].choices) :
          []
        if (!this.question.options.custom_fields[index]?.required) choices.unshift(null)
        return _.map(choices, custom_field => {
          return {
            value: custom_field,
            text: custom_field || '',
          }
        })
      },
      serialize() {
        let is_empty = _.isEmpty(_.compact([this.first_name, this.last_name, this.email, this.mobile]).join('').trim())
        if (this.question?.type == 'company') {
          is_empty = _.isEmpty(this.company)
        }
        if (is_empty) return null
        let result = {
          first_name: this.first_name,
          last_name: this.last_name,
          name: [this.first_name, this.last_name].join(' '),
          email: this.email,
          mobile: this.mobile,
        }
        if (this.recipient_id) result.id = this.recipient_id
        if (this.client_id) result.client_id = this.client_id
        if (this.membership) result.membership = this.membership
        if (this.options.date_of_birth) {
          result.date_of_birth = this.date_of_birth ? moment(this.date_of_birth, 'LL').format('YYYY-MM-DD') : null
        }
        _.each([
            'relationship',
            'pronouns',
            'company',
            'gender',
            'grade',
            'pickup_password',
            'medical',
            'friend',
            'other_phone',
            'cc_email',
            'custom_fields',
            'addons',
          ], option => {
          if (this.options[option]) result[option] = this[option]
        })

        if (this.options['medical_conditions']) {
          let medical_conditions = _.clone(this.medical_conditions)
          if (!isEmpty(this.other_medical_conditions)) {
            medical_conditions.push(this.other_medical_conditions.trim())
          }
          result['medical_conditions'] = medical_conditions
        }
        if (this.options['allergies']) {
          let allergies = _.clone(this.allergies)
          if (!isEmpty(this.other_allergies)) {
            allergies.push(this.other_allergies.trim())
          }
          result['allergies'] = allergies
        }
        if (this.options.address) {
          result.address = this.address
          result.address1 = this.address1
          result.city = this.city
          result.state_province = this.state_province
          result.zip_postal_code = this.zip_postal_code
        }
        if (this.options.zip_postal_code && !this.options.address) result.zip_postal_code = this.zip_postal_code
        if (this.options.can_pickup) result.can_pickup = this.can_pickup
        if (this.options.photo) {
          result.photo_umid = this.photo_umid
          result.photo_file = this.serializePhotoFile()
        }
        return result
      },
      hydrate() {
        let value = this.value || null
        if (!value && !!this.initialValue) value = this.initialValue
        this.relationship = value ? value.relationship || null : null
        this.company = value ? value.company || null : null
        this.pronouns = value ? value.pronouns || null : null
        this.can_pickup = this.options.can_pickup ? (value ? value.can_pickup || false : false) : null
        this.first_name = value ? value.first_name || null : null
        this.last_name = value ? value.last_name || null : null
        this.gender = value ? value.gender || null : null
        this.date_of_birth = value && value.date_of_birth ? moment(value.date_of_birth).format('LL') : null

        if (this.date_of_birth && this.isLive) {
          this.dd = moment(this.date_of_birth).format('DD')
          this.mm = moment(this.date_of_birth).format('MM')
          this.yyyy = moment(this.date_of_birth).format('YYYY')
        }

        this.grade = value ? value.grade || null : null
        this.pickup_password = value ? value.pickup_password || null : null
        this.friend = value ? value.friend || null : null
        this.medical = value ? value.medical || null : null
        this.mobile = value ? value.mobile || null : null
        this.email = value ? value.email || null : null
        this.other_phone = value ? value.other_phone || null : null
        this.address1 = value ? value.address1 || null : null
        this.city = value ? value.city || null : null
        this.state_province = value ? value.state_province : this.defaultStateProvince
        this.zip_postal_code = value ? value.zip_postal_code || null : null
        this.cc_email = value ? value.cc_email || null : null
        this.custom_fields = value ? value.custom_fields || [buildBlankCustomField()] : [buildBlankCustomField()]
        this.addons = value ? value.addons || [] : []
        this.deserializeMedicalConditions(value ? value.medical_conditions || [] : [])
        this.deserializeAllergies(value ? value.allergies || [] : [])

        if (this.options.addons && this.options.addons.length > 0) {
          let addons = this.options.addons
          addons.map((addon, index) => {
            if (this.addons[index] && this.addons[index].quantity == 1) {
              addon.disabled = false
              addon.quantity = 1
            }
          })
          this.addons = addons // could be better
        }

        // sometimes label is not getting set
        if (this.options.custom_fields) {
          this.options.custom_fields.map((custom_field, index) => {
            if (this.custom_fields[index] && !this.custom_fields[index].label) {
              this.custom_fields[index].label = custom_field.label
            }
          })
        }

        this.photo_umid = value && this.options.photo ? value.photo_umid || null : null
        this.photo_file = value && this.options.photo ? value.photo_file || null : null
        this.pond_files = !!this.photo_file ?
          [{
            source: this.photo_umid,
            options: {
              type: 'limbo',
              file: {
                name: this.photo_file.name,
                size: this.photo_file.size,
                type: this.photo_file.type,
              }
            }
          }] :
          []

        if (!!value) {
          // Nothing to do!
        }
        else if (!!this.organization && !!this.organization.connected_client && this.question.type == 'account_owner') {
          this.first_name = this.organization.connected_client.first_name
          this.last_name = this.organization.connected_client.last_name
          this.email = this.organization.connected_client.email
          this.mobile = this.organization.connected_client.mobile
        }
        else if (!!this.organization && !!this.organization.last_invoice_recipient && this.question.type == 'account_owner' && !this.organization.auth_user) {
          this.first_name = this.organization.last_invoice_recipient.first_name
          this.last_name = this.organization.last_invoice_recipient.last_name
          this.email = this.organization.last_invoice_recipient.email
          this.mobile = this.organization.last_invoice_recipient.mobile
        }
        if (this.lockNameEmail) {
          this.lockFirstName = !!this.first_name
          this.lockLastName = !!this.last_name
          this.lockEmail = !!this.email
          this.lockMobile = !!this.mobile
        }
        if (!!value) this.chosen_person = _.cloneDeep(value)
        this.$nextTick(this.emitIfDifferent)
      },
      fetchValidMembershipForPerson() {
        if (this.isDesign ||
          !this.organization ||
          !this.organization.connected_client ||
          !this.isEmailValid ||
          !!this.fetched_memberships[this.email] ||
          !!this.chosen_person && (!!this.chosen_person.membership || !!this.chosen_person.memberships))
          return

        let url = `/org/${this.organization.id}/client/friend_memberships?email=${encodeURIComponent(this.email)}`

        this.busy = true
        this.error = null
        axios.get(url)
        .then(response => {
          this.busy = false
          this.$set(this.fetched_memberships, this.email, response.data)
        })
        .catch(error => {
          this.busy = false
          //this.error = _.errorFromAjax(error)
        })
      },
      maybeUpdateNameFromMembership() {
        if (!this.membership ||
          this.membership.recipient_email != this.email ||
          this.membership.subtype != 'individual')
          return
        this.first_name = this.membership.recipient_first_name
        this.last_name = this.membership.recipient_last_name
      },
    },
    mounted() {
      window['questionPerson'+this.question.id] = this
      this.hydrate()
      this.ask_other_fields = buildAskOtherFieldOptions(!!this.question ? this.question.options || {} : {})
      if (this.proposeNames && localStorage.getItem('metas')) {
        this.metas = JSON.parse(localStorage.getItem('metas'))
        this.metas = [...this.persons, ...this.metas]
      }
    },
  }
</script>

